import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	adminApi,
	adminQueryKeys,
	BulkUserOperationRequest,
	UpdateUserRolesRequest,
	UserRole,
} from '../lib/admin-api';

/**
 * Hook for fetching users list with filtering and pagination
 */
export function useAdminUsers(
	params: {
		page?: number;
		limit?: number;
		search?: string;
		role?: UserRole;
		isActive?: boolean;
	} = {}
) {
	return useQuery({
		queryKey: adminQueryKeys.usersList(params),
		queryFn: () => adminApi.getUsers(params),
		staleTime: 30000, // 30 seconds
		gcTime: 300000, // 5 minutes
	});
}

/**
 * Hook for fetching user details by ID
 */
export function useAdminUserDetails(userId: string) {
	return useQuery({
		queryKey: adminQueryKeys.userDetails(userId),
		queryFn: () => adminApi.getUserById(userId),
		enabled: !!userId,
		staleTime: 60000, // 1 minute
		gcTime: 300000, // 5 minutes
	});
}

/**
 * Hook for updating user roles
 */
export function useUpdateUserRoles() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ userId, data }: { userId: string; data: UpdateUserRolesRequest }) =>
			adminApi.updateUserRoles(userId, data),
		onSuccess: (data, variables) => {
			// Invalidate and refetch users list
			queryClient.invalidateQueries({ queryKey: adminQueryKeys.users });

			// Update specific user details cache
			queryClient.setQueryData(adminQueryKeys.userDetails(variables.userId), (oldData: any) =>
				oldData ? { ...oldData, ...data } : data
			);

			// Invalidate statistics
			queryClient.invalidateQueries({ queryKey: adminQueryKeys.statistics });
		},
		onError: error => {
			console.error('Failed to update user roles:', error);
		},
	});
}

/**
 * Hook for deactivating users
 */
export function useDeactivateUser() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ userId, reason }: { userId: string; reason?: string }) =>
			adminApi.deactivateUser(userId, reason),
		onSuccess: (data, variables) => {
			// Invalidate and refetch users list
			queryClient.invalidateQueries({ queryKey: adminQueryKeys.users });

			// Update specific user details cache
			queryClient.setQueryData(adminQueryKeys.userDetails(variables.userId), (oldData: any) =>
				oldData ? { ...oldData, ...data } : data
			);

			// Invalidate statistics
			queryClient.invalidateQueries({ queryKey: adminQueryKeys.statistics });
		},
		onError: error => {
			console.error('Failed to deactivate user:', error);
		},
	});
}

/**
 * Hook for bulk role assignment
 */
export function useBulkAssignRoles() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: BulkUserOperationRequest) => adminApi.bulkAssignRoles(data),
		onSuccess: () => {
			// Invalidate all user-related queries
			queryClient.invalidateQueries({ queryKey: adminQueryKeys.users });
			queryClient.invalidateQueries({ queryKey: adminQueryKeys.statistics });
		},
		onError: error => {
			console.error('Failed to bulk assign roles:', error);
		},
	});
}

/**
 * Hook for fetching user management statistics
 */
export function useAdminStatistics() {
	return useQuery({
		queryKey: adminQueryKeys.statistics,
		queryFn: () => adminApi.getStatistics(),
		staleTime: 60000, // 1 minute
		gcTime: 300000, // 5 minutes
	});
}

/**
 * Hook for fetching audit logs
 */
export function useAuditLogs(
	params: {
		page?: number;
		limit?: number;
		userId?: string;
		action?: string;
		dateFrom?: string;
		dateTo?: string;
	} = {}
) {
	return useQuery({
		queryKey: adminQueryKeys.auditLogs(params),
		queryFn: () => adminApi.getAuditLogs(params),
		staleTime: 30000, // 30 seconds
		gcTime: 300000, // 5 minutes
	});
}

/**
 * Custom hook for admin user management with all operations
 */
export function useAdminUserManagement() {
	const updateUserRoles = useUpdateUserRoles();
	const deactivateUser = useDeactivateUser();
	const bulkAssignRoles = useBulkAssignRoles();

	return {
		// Mutations
		updateUserRoles: updateUserRoles.mutateAsync,
		deactivateUser: deactivateUser.mutateAsync,
		bulkAssignRoles: bulkAssignRoles.mutateAsync,

		// Loading states
		isUpdatingRoles: updateUserRoles.isPending,
		isDeactivating: deactivateUser.isPending,
		isBulkAssigning: bulkAssignRoles.isPending,

		// Error states
		updateError: updateUserRoles.error,
		deactivateError: deactivateUser.error,
		bulkError: bulkAssignRoles.error,
	};
}
